class NoiseGenerator {
    private audio: HTMLAudioElement;

    constructor() {
        this.audio = new Audio('/sai.mp3'); // Path to mp3 in the public folder
        this.audio.loop = true; // Loop the noise audio

        // Add attributes for background playback
        this.audio.setAttribute('playsinline', '');
        this.audio.setAttribute('webkit-playsinline', '');
        this.audio.setAttribute('preload', 'auto');
    }

    start() {
        this.audio.play().catch(console.error);
    }

    stop() {
        this.audio.pause();
        this.audio.currentTime = 0;
    }
}

export class Playback {
    private queue: string[] = [];
    private isPlaying = false;
    private audio: HTMLAudioElement | undefined;
    private noiseGenerator: NoiseGenerator;
    private audioContext: AudioContext | undefined;
    private panner: StereoPannerNode | undefined;
    private sourceNode: MediaElementAudioSourceNode | undefined;
    private wakeLock: any = null;

    constructor(private onChangeEnabled: (enabled: boolean) => void) {
        this.noiseGenerator = new NoiseGenerator();
    }

    isEnabled() {
        return !!this.audio;
    }

    /**
     * Initialize the audio player on user action
     * Otherwise, the browser on mobile will block the audio from playing
     * @param deviceId - The ID of the audio output device to use
     * @param stereo - The stereo setting, where -1 is left, 1 is right, and 0 is center
     */
    async enable(deviceId?: string, stereo: number = 0) {
        if (this.audio) {
            this.disable();
        }

        this.audio = new Audio();
        // Add attributes for background playback
        this.audio.setAttribute('playsinline', '');
        this.audio.setAttribute('webkit-playsinline', '');
        this.audio.setAttribute('preload', 'auto');

        if (deviceId) {
            await (this.audio as any).setSinkId(deviceId);
        }

        this.audioContext = new AudioContext();
        this.panner = this.audioContext.createStereoPanner();
        this.sourceNode = this.audioContext.createMediaElementSource(this.audio);

        // Connect the audio source to the panner and then to the destination
        this.sourceNode.connect(this.panner).connect(this.audioContext.destination);

        this.setPan(stereo);

        this.audio.addEventListener('ended', this.playNextInQueue.bind(this));

        // Add AudioContext state change listener to resume when suspended
        this.audioContext.addEventListener('statechange', this.handleAudioContextStateChange.bind(this));

        // Try to acquire wake lock
        this.requestWakeLock();

        // Add visibility change listener
        document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

        this.noiseGenerator.start();

        this.onChangeEnabled(true);
    }

    disable() {
        if (this.audio) {
            this.audio.removeEventListener('ended', this.playNextInQueue.bind(this));
            this.audio.remove();
            this.audio = undefined;

            if (this.audioContext) {
                this.audioContext.removeEventListener('statechange', this.handleAudioContextStateChange.bind(this));
                this.audioContext.close();
                this.audioContext = undefined;
            }

            document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
            this.releaseWakeLock();
            this.noiseGenerator.stop();
            this.onChangeEnabled(false);
        }
    }

    async add(audio: ArrayBuffer) {
        if (!this.isEnabled()) {
            return;
        }

        const blob = new Blob([audio], { type: 'audio/mp3' });
        const url = URL.createObjectURL(blob);
        this.queue.push(url);

        if (!this.isPlaying && this.audio) {
            this.isPlaying = true;
            await this.playNextInQueue();
        }
    }

    setPan(value: number) {
        if (this.panner) {
            // Set pan value (-1 for left, 1 for right, 0 for center)
            this.panner.pan.value = Math.max(-1, Math.min(1, value));
        }
    }

    private async playNextInQueue() {
        if (this.queue.length > 0 && this.audio) {
            const nextAudio = this.queue.shift();

            if (nextAudio) {
                this.audio.src = nextAudio;
                this.audio.playbackRate = this.getPlaybackRate();
                this.audio.play().catch(console.error);
            } else {
                this.isPlaying = false;
            }
        } else {
            this.isPlaying = false;
        }
    }

    private getPlaybackRate(): number {
        // if (this.queue.length > 4) {
        //     return 1.3;
        // }

        return 1.0;
    }

    private handleAudioContextStateChange() {
        if (this.audioContext?.state === 'suspended') {
            this.audioContext.resume().catch(console.error);
        }
    }

    private handleVisibilityChange() {
        if (document.visibilityState === 'visible') {
            // Resume audio context when page becomes visible
            this.audioContext?.resume().catch(console.error);
            this.requestWakeLock();
        } else {
            // When hidden, make sure audio can still play
            this.audioContext?.resume().catch(console.error);
        }
    }

    private async requestWakeLock() {
        if ('wakeLock' in navigator) {
            try {
                // Release any existing wake lock
                await this.releaseWakeLock();
                // Request a screen wake lock
                this.wakeLock = await (navigator as any).wakeLock.request('screen');
            } catch (err) {
                console.warn('Wake Lock error:', err);
            }
        }
    }

    private async releaseWakeLock() {
        if (this.wakeLock) {
            try {
                await this.wakeLock.release();
                this.wakeLock = null;
            } catch (err) {
                console.error('Release wake lock error:', err);
            }
        }
    }
}
